import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import CodeMirror, { ReactCodeMirrorRef } from '@uiw/react-codemirror';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { useCreatePage, useUpdatePage, usePage, useUploadAttachment, useTemplates, useTemplate, useSpaces, useDeletePage, useSpace } from '@/hooks';
import { Button, Input, Label, Select, Card, CardHeader, CardTitle, CardContent, Skeleton, SlashCommandMenu } from '@/components/ui';
import { MarkdownToolbar } from '@/components/ui/MarkdownToolbar';
import { useAuthStore } from '@/stores';
import toast from 'react-hot-toast';
import { Save, Eye, Edit, ArrowLeft, Upload, FileText, Trash2, FolderOpen, Cloud, CloudOff, FileTextIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'dompurify';
import { remarkWikiLink } from '@/lib/remark-wiki-link';

// Auto-save interval in milliseconds (30 seconds)
const AUTO_SAVE_INTERVAL = 30000;

// Draft storage key
const getDraftKey = (slug: string | undefined) => `page_draft_${slug || 'new'}`;

export function PageEditor() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isEditing = !!slug;
  const spaceSlug = searchParams.get('space');

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [visibility, setVisibility] = useState<'anonymous' | 'public' | 'editors' | 'private'>('public');
  const [tags, setTags] = useState('');
  const [spaceId, setSpaceId] = useState<number | null>(null);
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [hasDraft, setHasDraft] = useState(false);
  const editorRef = useRef<ReactCodeMirrorRef>(null);

  // Slash command state
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashPosition, setSlashPosition] = useState({ top: 0, left: 0 });
  const [slashFilter, setSlashFilter] = useState('');
  const slashStartRef = useRef<number | null>(null);

  const createPage = useCreatePage();
  const updatePage = useUpdatePage();
  const deletePage = useDeletePage();
  const uploadAttachment = useUploadAttachment();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Fetch spaces for selection
  const { data: spacesData } = useSpaces(1, 100);

  // Fetch space by slug if provided in URL (for new pages)
  const { data: spaceFromUrl } = useSpace(spaceSlug || '');

  // Fetch templates for selection
  const { data: templates } = useTemplates();

  // Fetch selected template content
  const { data: templateData } = useTemplate(selectedTemplate);

  // Fetch existing page if editing
  const { data: existingPage, isLoading: isLoadingPage } = usePage(slug || '');

  // Populate form when editing
  useEffect(() => {
    if (existingPage && isEditing) {
      setTitle(existingPage.title);
      setContent(existingPage.content);
      setVisibility(existingPage.visibility);
      setTags(existingPage.tags?.map(t => t.name).join(', ') || '');
      setSpaceId(existingPage.space_id || null);
    }
  }, [existingPage, isEditing]);

  // Set space from URL param for new pages
  useEffect(() => {
    if (!isEditing && spaceFromUrl && spaceFromUrl.id) {
      setSpaceId(spaceFromUrl.id);
    }
  }, [spaceFromUrl, isEditing]);

  // Apply template content when selected (only for new pages)
  useEffect(() => {
    if (!isEditing && templateData && templateData.content && !content) {
      setContent(templateData.content);
      toast.success(`已应用模板: ${templateData.title}`);
    }
  }, [templateData, isEditing, content]);

  // Load draft from localStorage on mount
  useEffect(() => {
    const draftKey = getDraftKey(slug);
    const savedDraft = localStorage.getItem(draftKey);
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        // Only load draft if it's newer than the page (for editing)
        if (!isEditing || !existingPage) {
          setTitle(draft.title || '');
          setContent(draft.content || '');
          setVisibility(draft.visibility || 'public');
          setTags(draft.tags || '');
          setSpaceId(draft.spaceId || null);
          setHasDraft(true);
          toast.success('已加载未保存的草稿');
        }
      } catch {
        // Invalid draft, ignore
        localStorage.removeItem(draftKey);
      }
    }
  }, [slug, isEditing, existingPage]);

  // Clear draft when page is loaded for editing
  useEffect(() => {
    if (isEditing && existingPage) {
      const draftKey = getDraftKey(slug);
      localStorage.removeItem(draftKey);
      setHasDraft(false);
    }
  }, [isEditing, existingPage, slug]);

  // Auto-save draft to localStorage
  useEffect(() => {
    // Don't auto-save empty content
    if (!title.trim() && !content.trim()) return;

    const saveDraft = () => {
      const draftKey = getDraftKey(slug);
      const draft = {
        title,
        content,
        visibility,
        tags,
        spaceId,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(draftKey, JSON.stringify(draft));
      setHasDraft(true);
    };

    const intervalId = setInterval(saveDraft, AUTO_SAVE_INTERVAL);
    return () => clearInterval(intervalId);
  }, [title, content, visibility, tags, spaceId, slug]);

  // Clear draft manually
  const handleClearDraft = useCallback(() => {
    const draftKey = getDraftKey(slug);
    localStorage.removeItem(draftKey);
    setHasDraft(false);
    toast.success('草稿已清除');
  }, [slug]);

  // Handle toolbar insert - must be defined before handlePaste and handleDrop
  const handleToolbarInsert = useCallback((before: string, after: string, placeholder?: string) => {
    const view = editorRef.current?.view;
    if (!view) {
      // Fallback: append to content
      setContent((prev) => prev + before + (placeholder || '') + after);
      return;
    }

    const selection = view.state.selection.main;
    const selectedText = view.state.doc.sliceString(selection.from, selection.to);
    const textToInsert = selectedText || placeholder || '';

    view.dispatch({
      changes: {
        from: selection.from,
        to: selection.to,
        insert: before + textToInsert + after,
      },
      selection: {
        anchor: selection.from + before.length,
        head: selection.from + before.length + textToInsert.length,
      },
    });

    view.focus();
  }, []);

  // Handle paste for images
  const handlePaste = useCallback(async (e: ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    for (const item of items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error('图片大小不能超过 10MB');
          return;
        }

        try {
          const result = await uploadAttachment.mutateAsync(file);
          handleToolbarInsert(result.markdown, '');
          toast.success(`已上传: ${result.attachment.filename}`);
        } catch (error: any) {
          toast.error(error.message || '上传失败');
        }
        return;
      }
    }
  }, [uploadAttachment, handleToolbarInsert]);

  // Set up paste listener
  useEffect(() => {
    const editorElement = editorRef.current?.view?.dom;
    if (editorElement) {
      const handler = handlePaste as unknown as EventListener;
      editorElement.addEventListener('paste', handler);
      return () => {
        editorElement.removeEventListener('paste', handler);
      };
    }
  }, [handlePaste]);

  // Global paste listener as fallback
  useEffect(() => {
    const handleGlobalPaste = (e: Event) => {
      const clipboardEvent = e as ClipboardEvent;
      // Only handle if we're not focused on an input
      const activeElement = document.activeElement;
      if (activeElement?.tagName === 'INPUT' || activeElement?.tagName === 'TEXTAREA') {
        return;
      }
      handlePaste(clipboardEvent);
    };

    document.addEventListener('paste', handleGlobalPaste);
    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
    };
  }, [handlePaste]);

  // Check if user can create/edit pages
  if (!user || (user.role !== 'editor' && user.role !== 'admin')) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground">您没有{isEditing ? '编辑' : '创建'}页面的权限</p>
        </CardContent>
      </Card>
    );
  }

  // Show loading state when fetching existing page
  if (isEditing && isLoadingPage) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // If editing but page not found
  if (isEditing && !existingPage && !isLoadingPage) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground mb-4">页面不存在</p>
          <Button variant="outline" onClick={() => navigate('/pages')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            返回列表
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Check if user can edit this specific page
  if (isEditing && existingPage) {
    const canEdit = user.role === 'admin' || existingPage.author_id === user.id;
    if (!canEdit) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-muted-foreground mb-4">您没有权限编辑此页面</p>
            <Button variant="outline" onClick={() => navigate(`/pages/${slug}`)}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              返回页面
            </Button>
          </CardContent>
        </Card>
      );
    }
  }

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error('请输入标题');
      return;
    }

    if (!content.trim()) {
      toast.error('请输入内容');
      return;
    }

    setSaving(true);

    try {
      const tagList = tags
        .split(',')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);

      if (isEditing) {
        // Update existing page
        await updatePage.mutateAsync({
          slug: slug!,
          data: {
            title: title.trim(),
            content: content.trim(),
            visibility,
            tags: tagList.length > 0 ? tagList : undefined,
            space_id: spaceId,
          },
        });
        toast.success('页面更新成功');
      } else {
        // Create new page
        const result = await createPage.mutateAsync({
          title: title.trim(),
          content: content.trim(),
          visibility,
          tags: tagList.length > 0 ? tagList : undefined,
          space_id: spaceId,
        });
        toast.success('页面创建成功');
        navigate(`/pages/${result.slug}`);
        return;
      }

      navigate(`/pages/${slug}`);
    } catch (error: any) {
      toast.error(error.message || (isEditing ? '更新失败' : '创建失败'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!slug || !existingPage) return;

    setDeleting(true);
    try {
      await deletePage.mutateAsync(slug);
      toast.success('页面已删除');
      navigate('/pages');
    } catch (error: any) {
      toast.error(error.message || '删除失败');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const sanitizedContent = DOMPurify.sanitize(content);

  // Calculate word count
  const wordCount = {
    characters: content.length,
    charactersNoSpaces: content.replace(/\s/g, '').length,
    words: content.trim() ? content.trim().split(/\s+/).length : 0,
    lines: content.split('\n').length,
  };

  // Handle drag and drop for files
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length === 0) return;

    const file = files[0];

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('文件大小不能超过 10MB');
      return;
    }

    try {
      const result = await uploadAttachment.mutateAsync(file);
      // Insert the markdown at cursor position
      handleToolbarInsert(result.markdown, '');
      toast.success(`已上传: ${result.attachment.filename}`);
    } catch (error: any) {
      toast.error(error.message || '上传失败');
    }
  }, [uploadAttachment, handleToolbarInsert]);

  // Handle slash command selection
  const handleSlashSelect = useCallback((command: { action: () => string }) => {
    const view = editorRef.current?.view;
    if (!view || slashStartRef.current === null) {
      setContent((prev) => prev + command.action());
      setShowSlashMenu(false);
      return;
    }

    const insertText = command.action();
    const cursorPos = view.state.selection.main.head;

    // Delete from slash start to current cursor (including filter text)
    view.dispatch({
      changes: {
        from: slashStartRef.current,
        to: cursorPos,
        insert: insertText,
      },
      selection: {
        anchor: slashStartRef.current + insertText.length,
      },
    });

    view.focus();
    setShowSlashMenu(false);
    slashStartRef.current = null;
  }, []);

  // Handle content change with slash command detection
  const handleContentChange = useCallback((value: string) => {
    setContent(value);

    const view = editorRef.current?.view;
    if (!view) return;

    const cursorPos = view.state.selection.main.head;
    const textBefore = value.slice(0, cursorPos);

    // Check if we're after a slash at line start or after newline
    const slashMatch = textBefore.match(/(?:^|\n)(\s*)(\/)([^\/\n]*)$/);

    if (slashMatch) {
      const slashIndex = textBefore.lastIndexOf('/');
      const filterText = textBefore.slice(slashIndex + 1);

      // Get cursor coordinates for menu position
      const coords = view.coordsAtPos?.(cursorPos);
      if (coords) {
        setSlashPosition({
          top: coords.bottom + 5,
          left: Math.min(coords.left, window.innerWidth - 300),
        });
      }

      slashStartRef.current = slashIndex;
      setSlashFilter(filterText);
      setShowSlashMenu(true);
    } else {
      // Close menu if we've moved away from slash command
      setShowSlashMenu(false);
      slashStartRef.current = null;
    }
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{isEditing ? '编辑页面' : '新建页面'}</h1>
        <div className="flex items-center space-x-3">
          {/* Auto-save status */}
          <div className="flex items-center text-sm text-muted-foreground">
            {hasDraft ? (
              <>
                <Cloud className="h-4 w-4 mr-1 text-green-500" />
                <span>已自动保存</span>
              </>
            ) : (
              <>
                <CloudOff className="h-4 w-4 mr-1" />
                <span>未保存</span>
              </>
            )}
            {hasDraft && (
              <button
                onClick={handleClearDraft}
                className="ml-2 text-xs text-muted-foreground hover:text-destructive transition-colors"
              >
                清除草稿
              </button>
            )}
          </div>
          {isEditing && (
            <Button
              variant="ghost"
              className="text-destructive hover:text-destructive"
              onClick={() => setShowDeleteModal(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              删除
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setPreview(!preview)}
          >
            {preview ? (
              <>
                <Edit className="h-4 w-4 mr-2" />
                编辑
              </>
            ) : (
              <>
                <Eye className="h-4 w-4 mr-2" />
                预览
              </>
            )}
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? '保存中...' : '保存'}
          </Button>
        </div>
      </div>

      {/* Word count bar */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground border-b pb-3">
        <div className="flex items-center gap-1">
          <FileTextIcon className="h-4 w-4" />
          <span>字数: {wordCount.characters}</span>
        </div>
        <span>|</span>
        <span>词数: {wordCount.words}</span>
        <span>|</span>
        <span>行数: {wordCount.lines}</span>
      </div>

      {/* Preview Mode - Full Width */}
      {preview && (
        <div className="w-full">
          <Card>
            <CardContent className="p-6">
              {/* Tags above title */}
              {tags && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {tags.split(',').map((tag, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-muted rounded-full text-sm"
                    >
                      {tag.trim()}
                    </span>
                  ))}
                </div>
              )}
              <div className="prose prose-slate dark:prose-invert max-w-none">
                <h1>{title || '无标题'}</h1>
                <div className="markdown-content">
                  {content ? (
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkWikiLink]}
                      rehypePlugins={[rehypeHighlight, rehypeRaw]}
                    >
                      {sanitizedContent}
                    </ReactMarkdown>
                  ) : (
                    <p className="text-muted-foreground">暂无内容</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Editor Mode */}
      {!preview && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Editor */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">标题</Label>
                    <Input
                      id="title"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="输入页面标题"
                      maxLength={200}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="content">内容 (Markdown)</Label>
                    <div
                      className={`border rounded-md overflow-hidden transition-colors ${
                        isDragging ? 'border-primary bg-primary/5' : ''
                      }`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                    >
                      <MarkdownToolbar
                        editorRef={editorRef}
                        onInsert={handleToolbarInsert}
                      />
                      <div className="relative">
                        <CodeMirror
                          ref={editorRef}
                          value={content}
                          height="500px"
                          extensions={[markdown({ base: markdownLanguage })]}
                          onChange={handleContentChange}
                          placeholder="使用 Markdown 编写内容...&#10;&#10;提示：输入 / 打开命令菜单&#10;支持拖拽或 Ctrl+V 粘贴图片上传"
                          theme="light"
                        />
                        {isDragging && (
                          <div className="absolute inset-0 bg-primary/10 flex items-center justify-center pointer-events-none">
                            <div className="text-center">
                              <Upload className="h-12 w-12 mx-auto mb-2 text-primary" />
                              <p className="text-lg font-medium text-primary">拖放文件到此处上传</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      支持拖拽或 Ctrl+V 粘贴上传图片、文档等文件（最大 10MB）| 自动保存草稿每 30 秒
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Settings */}
          <div className="space-y-6">
            {/* Template selection - only for new pages */}
            {!isEditing && templates && templates.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    模板
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select
                    value={selectedTemplate}
                    onChange={(e) => setSelectedTemplate(e.target.value)}
                  >
                    <option value="">选择模板（可选）</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.slug}>
                        {template.title}
                      </option>
                    ))}
                  </Select>
                  {selectedTemplate && (
                    <p className="text-xs text-muted-foreground mt-2">
                      选择模板后将填充预设内容
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">设置</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="space" className="flex items-center gap-2">
                    <FolderOpen className="h-4 w-4" />
                    所属空间
                  </Label>
                  <Select
                    id="space"
                    value={spaceId || ''}
                    onChange={(e) => setSpaceId(e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">无（全局页面）</option>
                    {spacesData?.items?.map((space) => (
                      <option key={space.id} value={space.id}>
                        {space.name}
                      </option>
                    ))}
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    选择空间后，页面将归属于该空间
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="visibility">可见性</Label>
                  <Select
                    id="visibility"
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value as any)}
                  >
                    <option value="anonymous">匿名公开 - 无需登录即可查看</option>
                    <option value="public">公开 - 登录用户可见</option>
                    <option value="editors">编辑者可见 - 编辑者和管理员可见</option>
                    <option value="private">私有 - 仅作者和管理员可见</option>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tags">标签 (逗号分隔)</Label>
                  <Input
                    id="tags"
                    value={tags}
                    onChange={(e) => setTags(e.target.value)}
                    placeholder="标签1, 标签2, 标签3"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>删除页面</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                确定要删除页面 "{existingPage?.title}" 吗？此操作不可撤销。
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  取消
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={deleting}
                >
                  {deleting ? '删除中...' : '确认删除'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Slash Command Menu */}
      <SlashCommandMenu
        visible={showSlashMenu}
        position={slashPosition}
        filter={slashFilter}
        onSelect={handleSlashSelect}
        onClose={() => {
          setShowSlashMenu(false);
          slashStartRef.current = null;
        }}
      />
    </div>
  );
}
